/**
 * The acknowledgement tracker of one SOP version (plan docs/plans/lms-plan.md
 * 11.3 "The SOPs tab's tracker shows, per version, who acknowledged when and
 * who is pending, with a CSV export"; 18.25, 18.27; WP-46).
 *
 * GET /admin/sops/:id/versions/:vid/acknowledgements answers the two lists
 * over the people the admin reaches (G14). ONE definition serves this screen
 * and the CSV (18.27, sops/tracker.js): the newest acknowledgement per person
 * counts, and it answers for them only while it is current. So the lists can
 * OVERLAP: a person whose acknowledgement has expired (or was superseded) and
 * who holds an open renewal is in both, and the counts add up to more than
 * the people. The tracker says so and marks those people in each list.
 *
 * The CSV is WP-39's GET /admin/reports/sops/:id/versions/:vid/acknowledgements.csv,
 * saved as a file (the same export an inspector is handed).
 *
 * The route carries the version's effective date and, per acknowledgement, its
 * expiry, whether it is current and whether it was signed with the password
 * re-check (docs/plans/sop-import-plan.md 4.4 item 4), so the panel shows what
 * the CSV prints. An older api without them shows the panel as before.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Download, Loader2, X } from 'lucide-react';
import { iconBtnCls, outlineBtnCls } from '../../../ui/styles';
import { useToast } from '../../../ui/Toast';
import { apiErrorMessage } from '../../api/client';
import { saveBlob } from '../../api/certificates';
import { downloadSopAcknowledgementsCsv, getSopAcknowledgements } from '../../api/sops';
import type { SopAcknowledgements } from '../../api/sops';
import { Pill } from '../../components/StatusPill';
import { formatSopDay } from './labels';
import {
  cardCls,
  ErrorNote,
  formatDay,
  metaCls,
  microLabelCls,
  personName,
  sectionTitleCls,
  tableCardCls,
  tableCls,
  tbodyCls,
  tdCls,
  thCls,
  theadCls,
} from '../../components/pickers/shared';

export interface AckTrackerProps {
  sopId: string;
  versionId: string;
  versionLabel: string;
  onClose: () => void;
}

/** The people who are in both lists (18.27: an expired or superseded acknowledgement with an open assignment). */
export function overlapIds(data: Pick<SopAcknowledgements, 'acknowledged' | 'pending'>): Set<string> {
  const pending = new Set(data.pending.map((p) => p.user.id));
  return new Set(data.acknowledged.filter((a) => pending.has(a.user.id)).map((a) => a.user.id));
}

const SOURCE_LABELS: Record<string, string> = {
  campaign: 'SOP send',
  retrain: 'Retrain',
  acknowledge: 'Acknowledge',
  renewal: 'Renewal',
  profile: 'Profile',
  manual: 'Assigned',
  onboarding: 'Onboarding',
};

export function AckTracker({ sopId, versionId, versionLabel, onClose }: AckTrackerProps) {
  const { toast } = useToast();
  const [data, setData] = useState<SopAcknowledgements | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [exporting, setExporting] = useState(false);
  const request = useRef(0);

  useEffect(() => {
    const id = ++request.current;
    setLoading(true);
    setError(null);
    getSopAcknowledgements(sopId, versionId)
      .then((answer) => {
        if (id !== request.current) return;
        setData({
          ...answer,
          acknowledged: Array.isArray(answer?.acknowledged) ? answer.acknowledged : [],
          pending: Array.isArray(answer?.pending) ? answer.pending : [],
        });
      })
      .catch((err) => {
        if (id !== request.current) return;
        setData(null);
        setError(apiErrorMessage(err, 'The acknowledgements could not be loaded.'));
      })
      .finally(() => {
        if (id === request.current) setLoading(false);
      });
  }, [sopId, versionId, attempt]);

  const both = useMemo(() => (data ? overlapIds(data) : new Set<string>()), [data]);

  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const file = await downloadSopAcknowledgementsCsv(sopId, versionId);
      saveBlob(file.blob, file.filename);
    } catch (err) {
      toast(apiErrorMessage(err, 'The CSV could not be exported.'), { type: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const counts = data?.counts;

  return (
    <section className={`${cardCls} space-y-4`} aria-label={`Acknowledgements of version ${versionLabel}`} data-testid="lms-sop-ack-tracker">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className={sectionTitleCls}>{`Acknowledgements: version ${versionLabel}`}</h3>
          {data?.version?.effective_on && (
            <p className={metaCls} data-testid="lms-sop-ack-effective">{`Effective ${formatSopDay(data.version.effective_on)}`}</p>
          )}
          {data?.campaign && (
            <p className={metaCls}>{`Sent ${formatDay(data.campaign.sent_at)} to ${data.campaign.recipient_count.toLocaleString()}, due ${formatDay(data.campaign.due_at)}`}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className={outlineBtnCls()} onClick={exportCsv} disabled={exporting || !data} data-testid="lms-sop-ack-csv">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Download className="h-4 w-4" aria-hidden="true" />}
            Export CSV
          </button>
          <button type="button" className={iconBtnCls} onClick={onClose} aria-label="Close the acknowledgements">
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>

      {loading && !data && (
        <div className="flex justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
          <span className="sr-only">Loading the acknowledgements</span>
        </div>
      )}
      {error && (
        <ErrorNote testId="lms-sop-ack-error">
          {error}{' '}
          <button type="button" className="font-medium underline" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </ErrorNote>
      )}

      {data && counts && (
        <>
          <dl className="grid grid-cols-3 gap-3 sm:max-w-md" aria-live="polite">
            <div>
              <dt className={microLabelCls}>Sent to</dt>
              <dd className="text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-100" data-testid="lms-sop-ack-sent">
                {counts.sent_to === null ? '\u2014' : counts.sent_to.toLocaleString()}
              </dd>
            </div>
            <div>
              <dt className={microLabelCls}>Acknowledged</dt>
              <dd className="text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-100" data-testid="lms-sop-ack-count">
                {counts.acknowledged.toLocaleString()}
              </dd>
            </div>
            <div>
              <dt className={microLabelCls}>Pending</dt>
              <dd className="text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-100" data-testid="lms-sop-pending-count">
                {counts.pending.toLocaleString()}
              </dd>
            </div>
          </dl>
          {both.size > 0 && (
            <p className="text-sm text-amber-800 dark:text-amber-300" data-testid="lms-sop-ack-overlap">
              {`${both.size.toLocaleString()} ${both.size === 1 ? 'person is' : 'people are'} in both lists: their acknowledgement is no longer current (it expired or was replaced) and they are assigned again. The counts include them twice.`}
            </p>
          )}
          <p className={metaCls}>Only people who can reach a site you manage are listed.</p>

          <div className="grid gap-4 xl:grid-cols-2">
            <div className="space-y-2">
              <h4 className={microLabelCls}>{`Acknowledged (${data.acknowledged.length.toLocaleString()})`}</h4>
              {data.acknowledged.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Nobody has acknowledged this version yet.</p>
              ) : (
                <div className={tableCardCls}>
                  <table className={tableCls} data-testid="lms-sop-ack-list">
                    <thead className={theadCls}>
                      <tr>
                        <th scope="col" className={thCls}>Person</th>
                        <th scope="col" className={thCls}>Signed</th>
                        <th scope="col" className={thCls}>State</th>
                      </tr>
                    </thead>
                    <tbody className={tbodyCls}>
                      {data.acknowledged.map((a) => (
                        <tr key={a.completion_id} data-testid="lms-sop-ack-row">
                          <td className={tdCls}>
                            <span className="block font-medium">{personName(a.user)}</span>
                            {a.user.email && <span className={metaCls}>{a.user.email}</span>}
                            {a.user.deleted && <span className={`${metaCls} ml-1`}>(left)</span>}
                          </td>
                          <td className={`${tdCls} whitespace-nowrap`}>
                            {formatDay(a.completed_at)}
                            <span className={`${metaCls} block`}>
                              {`${a.kind === 'quiz' ? 'Quiz passed' : 'Read and signed'}${a.reauth ? ', password re-checked' : ''}`}
                            </span>
                            {a.expires_at && !a.superseded && (
                              <span className={`${metaCls} block`} data-testid="lms-sop-ack-expiry">
                                {`${a.current === false ? 'Expired' : 'Valid until'} ${formatDay(a.expires_at)}`}
                              </span>
                            )}
                          </td>
                          <td className={tdCls}>
                            {both.has(a.user.id) ? (
                              <Pill tone="amber">Not current: assigned again</Pill>
                            ) : a.superseded ? (
                              <Pill tone="gray">Superseded</Pill>
                            ) : a.current === false ? (
                              <Pill tone="amber">Expired</Pill>
                            ) : (
                              <Pill tone="green">Acknowledged</Pill>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <h4 className={microLabelCls}>{`Pending (${data.pending.length.toLocaleString()})`}</h4>
              {data.pending.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Nobody is waiting on this version.</p>
              ) : (
                <div className={tableCardCls}>
                  <table className={tableCls} data-testid="lms-sop-pending-list">
                    <thead className={theadCls}>
                      <tr>
                        <th scope="col" className={thCls}>Person</th>
                        <th scope="col" className={thCls}>Due</th>
                        <th scope="col" className={thCls}>State</th>
                      </tr>
                    </thead>
                    <tbody className={tbodyCls}>
                      {data.pending.map((p) => (
                        <tr key={p.assignment_id} data-testid="lms-sop-pending-row">
                          <td className={tdCls}>
                            <span className="block font-medium">{personName(p.user)}</span>
                            {p.user.email && <span className={metaCls}>{p.user.email}</span>}
                            {p.user.deleted && <span className={`${metaCls} ml-1`}>(left)</span>}
                          </td>
                          <td className={`${tdCls} whitespace-nowrap`}>
                            {formatDay(p.due_at)}
                            <span className={`${metaCls} block`}>
                              {`${SOURCE_LABELS[p.source] ?? p.source}${p.mode === 'acknowledge' ? ', acknowledge' : ''}`}
                            </span>
                          </td>
                          <td className={tdCls}>
                            <div className="flex flex-wrap gap-1.5">
                              {p.overdue ? <Pill tone="red">Overdue</Pill> : <Pill tone="primary">Pending</Pill>}
                              {both.has(p.user.id) && <Pill tone="amber">Acknowledged before</Pill>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
}

export default AckTracker;
